import React, { useEffect, useMemo, useRef, useState } from "react";
import PageLayout from "./PageLayout";
import { Account } from "./TopMenuBar";
import { useViewport } from "../hooks/useViewport";

const API_BASE = (import.meta as any).env?.VITE_API_BASE || "http://localhost:8100";
const LEADERBOARD_BG = "LeaderboardBackground.png";

function getAssetPath(path: string): string {
  const cleanPath = path.replace(/^\//, "");
  const isBrowser = typeof window !== "undefined";

  const cap = isBrowser ? (window as any).Capacitor : null;
  const isNative =
    Boolean(cap) &&
    (typeof cap.isNativePlatform === "function"
      ? cap.isNativePlatform()
      : typeof cap.getPlatform === "function"
        ? cap.getPlatform() === "ios" || cap.getPlatform() === "android"
        : cap.platform === "ios" || cap.platform === "android");

  if (isNative) {
    try {
      const baseUrl = new URL(window.location.href);
      return baseUrl.origin + "/" + cleanPath;
    } catch (e) {
      console.warn("[getAssetPath] Failed to construct URL, using fallback:", e);
      return "/" + cleanPath;
    }
  }

  return "./" + cleanPath;
}

function Card(props: { title?: string; children: React.ReactNode; footer?: React.ReactNode; isMobileLandscape?: boolean }) {
  const { isMobileLandscape = false } = props;
  return (
    <div className={isMobileLandscape ? "w-full rounded-xl shadow-xl border-4" : "mx-auto max-w-2xl rounded-2xl shadow-xl border-4"} style={{
      background: "linear-gradient(to bottom, #f7b267 0%, #f4a261 100%)",
      borderColor: "#8b6542"
    }}>
      <div className={isMobileLandscape ? "p-3" : "p-6"}>
        {props.title && <h2 className={isMobileLandscape ? "text-sm font-bold mb-1.5" : "text-2xl font-bold mb-4"} style={{ color: "#3e2615" }}>{props.title}</h2>}
        <div className={isMobileLandscape ? "space-y-1.5" : "space-y-3"}>{props.children}</div>
        {props.footer && <div className={isMobileLandscape ? "mt-2 border-t pt-1.5 text-xs" : "mt-6 border-t pt-4 text-sm"} style={{ borderColor: "#8b6542", color: "#3e2615" }}>{props.footer}</div>}
      </div>
    </div>
  );
}

interface DeleteAccountProps {
  account?: Account | null;
  onLogout?: () => void;
  onAccountUpdate?: (account: Account | null) => void;
}

export default function DeleteAccount({ account, onLogout, onAccountUpdate }: DeleteAccountProps) {
  const viewport = useViewport({ mobileBreakpoint: 1024 });
  const isMobileLandscape = !viewport.isPortrait && viewport.isMobile;
  const isAndroid = typeof window !== "undefined" && (window as any).Capacitor && (window as any).Capacitor.getPlatform() === "android";
  const androidInsetLeft = isAndroid ? Math.round(viewport.width * (70 / 1920)) : 0;
  const androidInsetRight = isAndroid ? Math.round(viewport.width * (112 / 1920)) : 0;

  const [email, setEmail] = useState(account?.email || "");
  const [reason, setReason] = useState("");
  const [otherReason, setOtherReason] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [message, setMessage] = useState("");
  const isAuthenticated = Boolean(account);
  const [pendingPayload, setPendingPayload] = useState<{ email: string; reason: string | null } | null>(null);
  const [loginOpen, setLoginOpen] = useState(false);
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [loginSubmitting, setLoginSubmitting] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [showTopIndicator, setShowTopIndicator] = useState(false);
  const [showBottomIndicator, setShowBottomIndicator] = useState(false);

  const reasonOptions = useMemo(() => [
    { value: "", label: "Choose a reason (optional)" },
    { value: "No longer playing", label: "No longer playing" },
    { value: "Privacy concerns", label: "Privacy concerns" },
    { value: "Using a different account", label: "Using a different account" },
    { value: "Other", label: "Other" },
  ], []);

  const resolvedReason = reason === "Other" ? otherReason.trim() : reason;

  useEffect(() => {
    if (account?.email) {
      setEmail((current) => current || account.email);
      setLoginEmail((current) => current || account.email);
    }
  }, [account?.email]);

  const updateScrollIndicators = (el: HTMLDivElement | null) => {
    if (!el) return;
    const isAtTop = el.scrollTop === 0;
    const isAtBottom = el.scrollHeight - el.scrollTop <= el.clientHeight + 1;
    setShowTopIndicator(!isAtTop);
    setShowBottomIndicator(!isAtBottom);
  };

  useEffect(() => {
    if (scrollRef.current) {
      requestAnimationFrame(() => updateScrollIndicators(scrollRef.current));
    }
  }, [isMobileLandscape]);

  const submitDeletion = async (payload: { email: string; reason: string | null }) => {
    setStatus("loading");
    setMessage("");

    try {
      const res = await fetch(`${API_BASE}/delete-account-request`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || "Failed to submit request");
      }

      setStatus("success");
      setMessage("We received your request. Deletions are processed within 30 days.");
      setReason("");
      setOtherReason("");
    } catch (err: any) {
      setStatus("error");
      setMessage(err?.message || "Unable to submit your request. Please try again or email support@slantwise.ai.");
    }
  };

  const fetchAccountAndUpdate = async () => {
    const res = await fetch(`${API_BASE}/account`, {
      credentials: "include",
      headers: { "Content-Type": "application/json" },
    });
    if (!res.ok) return null;
    const data = await res.json();
    setEmail((current) => current || data.email || "");
    onAccountUpdate?.(data);
    window.dispatchEvent(new CustomEvent("accountUpdated", { detail: data }));
    return data as Account;
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginSubmitting(true);
    setLoginError("");
    try {
      const res = await fetch(`${API_BASE}/login`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ identifier: loginEmail, password: loginPassword }),
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || "Login failed");
      }
      // Clear guest flags like other login flows
      try {
        const { clearGuestSession, clearLoggedOutFlag } = await import("../lib/guest-storage");
        await clearGuestSession();
        await clearLoggedOutFlag();
        sessionStorage.removeItem("regularUserLoggedOut");
      } catch {
        // non-critical
      }
      const freshAccount = await fetchAccountAndUpdate();
      setLoginOpen(false);
      setLoginSubmitting(false);
      setLoginPassword("");
      if (pendingPayload) {
        const payload = {
          ...pendingPayload,
          email: (freshAccount?.email || pendingPayload.email || "").trim(),
        };
        setPendingPayload(null);
        await submitDeletion(payload);
      }
    } catch (err: any) {
      setLoginSubmitting(false);
      setLoginError(err?.message || "Login failed. Please try again.");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const payload = {
      email: (account?.email || email).trim(),
      reason: resolvedReason || null,
    };
    if (!isAuthenticated) {
      setPendingPayload(payload);
      setLoginEmail(payload.email);
      setLoginOpen(true);
      return;
    }
    await submitDeletion(payload);
  };

  useEffect(() => {
    if (account && pendingPayload && !loginOpen) {
      const payload = {
        ...pendingPayload,
        email: (account.email || pendingPayload.email).trim(),
      };
      setPendingPayload(null);
      void submitDeletion(payload);
    }
  }, [account, pendingPayload, loginOpen]);

  return (
    <PageLayout account={account} onLogout={onLogout}>
      {isAndroid && (
        <div
          className="fixed inset-0 w-screen h-screen overflow-hidden"
          style={{ zIndex: 0, pointerEvents: "none" }}
          aria-hidden="true"
        >
          <img
            src={getAssetPath(LEADERBOARD_BG)}
            alt=""
            className="absolute inset-0 w-full h-full"
            style={{ objectFit: "cover", objectPosition: "center" }}
            draggable={false}
          />
        </div>
      )}

      <div
        className={isMobileLandscape ? "fixed z-40 p-2 flex flex-col" : "px-2 md:px-6 pb-4 space-y-4"}
        style={isMobileLandscape ? {
          top: isAndroid && isMobileLandscape ? "32px" : "env(safe-area-inset-top, 0px)",
          left: isAndroid
            ? `calc(env(safe-area-inset-left, 0px) + 32px + 112px + ${androidInsetLeft}px)`
            : "calc(env(safe-area-inset-left, 0px) + 32px + 112px)",
          right: isAndroid
            ? `calc(env(safe-area-inset-right, 0px) + 48px + ${androidInsetRight}px)`
            : "calc(env(safe-area-inset-right, 0px) + 48px)",
          bottom: "calc(env(safe-area-inset-bottom, 8px) - 14px + 50px)",
          overflow: "visible",
          marginLeft: "-20px",
          marginRight: 0,
        } : (isAndroid ? { paddingLeft: `${androidInsetLeft}px`, paddingRight: `${androidInsetRight}px` } : {})}
      >
        <div className={isMobileLandscape ? "relative flex-1 min-h-0" : ""} style={isMobileLandscape ? { overflow: "visible" } : {}}>
          <div
            ref={scrollRef}
            onScroll={(e) => updateScrollIndicators(e.currentTarget)}
            className={isMobileLandscape ? "h-full overflow-y-auto overflow-x-auto pr-1 flex flex-col gap-2" : "space-y-4"}
            style={isMobileLandscape ? { paddingRight: "4px" } : {}}
          >
        <Card title="Delete Your Goldrush Account" isMobileLandscape={isMobileLandscape}>
          <p className={isMobileLandscape ? "text-xs" : "text-base"} style={{ color: "#3e2615", opacity: 0.9 }}>
            You can request deletion of your Goldrush account and personal data at any time. Deletion requests are processed within 30 days.
          </p>

          <div className={isMobileLandscape ? "grid grid-cols-2 gap-2 text-[11px]" : "text-sm space-y-1.5"} style={{ color: "#3e2615" }}>
            <div className={isMobileLandscape ? "space-y-1" : ""}>
              <p className="font-semibold" style={{ color: "#3e2615" }}>What will be deleted:</p>
              <ul className="list-disc pl-5 space-y-0.5">
                <li>Your account profile (username, email)</li>
                <li>Game progress and statistics</li>
                <li>In-game currency balances</li>
                <li>Tournament history</li>
              </ul>
            </div>
            <div className={isMobileLandscape ? "space-y-1" : ""}>
              <p className="font-semibold" style={{ color: "#3e2615" }}>What may be retained:</p>
              <ul className="list-disc pl-5 space-y-0.5">
                <li>Records required for legal, security, or fraud-prevention (kept up to 90 days)</li>
              </ul>
            </div>
            <div className={isMobileLandscape ? "col-span-2" : ""}>
              <p className="font-semibold" style={{ color: "#3e2615" }}>How to request deletion:</p>
              <p className="mt-1" style={{ opacity: 0.9 }}>
                Submit the form below with the email on your account. You can also email <a href="mailto:support@slantwise.ai?subject=Account%20Deletion%20Request" className="underline">support@slantwise.ai</a>.
              </p>
            </div>
          </div>
        </Card>

        <Card title="Request deletion" isMobileLandscape={isMobileLandscape}>
          <form className={isMobileLandscape ? "space-y-1.5" : "space-y-3"} onSubmit={handleSubmit}>
            <div>
              <label className={isMobileLandscape ? "block text-[10px] font-medium mb-0.5" : "block text-sm font-medium mb-1"} style={{ color: "#3e2615" }}>
                Email address
              </label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                readOnly={!!account?.email}
                className={isMobileLandscape ? "w-full rounded px-2 py-1 text-[12px] outline-none transition-all" : "w-full rounded-xl p-3 outline-none transition-all"}
                style={{
                  border: "2px solid #c4a07a",
                  background: "#ffffff",
                  color: "#3e2615",
                  minHeight: isMobileLandscape ? "32px" : "auto",
                }}
                placeholder={isAuthenticated ? undefined : "Sign in to auto-fill your email"}
                onFocus={(e) => { e.target.style.borderColor = "#d4a05f"; }}
                onBlur={(e) => { e.target.style.borderColor = "#c4a07a"; }}
              />
            </div>

            <div>
              <label className={isMobileLandscape ? "block text-[10px] font-medium mb-0.5" : "block text-sm font-medium mb-1"} style={{ color: "#3e2615" }}>
                Reason (optional)
              </label>
              <select
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className={isMobileLandscape ? "w-full rounded px-2 py-1 text-[12px] outline-none transition-all" : "w-full rounded-xl p-3 outline-none transition-all"}
                style={{
                  border: "2px solid #c4a07a",
                  background: "#ffffff",
                  color: "#3e2615",
                  minHeight: isMobileLandscape ? "32px" : "auto",
                }}
                onFocus={(e) => { e.target.style.borderColor = "#d4a05f"; }}
                onBlur={(e) => { e.target.style.borderColor = "#c4a07a"; }}
              >
                {reasonOptions.map((opt) => (
                  <option key={opt.value || "none"} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            {reason === "Other" && (
              <div>
                <label className={isMobileLandscape ? "block text-[10px] font-medium mb-0.5" : "block text-sm font-medium mb-1"} style={{ color: "#3e2615" }}>
                  Short note (optional)
                </label>
                <input
                  type="text"
                  value={otherReason}
                  onChange={(e) => setOtherReason(e.target.value)}
                  maxLength={200}
                  className={isMobileLandscape ? "w-full rounded px-2 py-1 text-[12px] outline-none transition-all" : "w-full rounded-xl p-3 outline-none transition-all"}
                  style={{
                    border: "2px solid #c4a07a",
                    background: "#ffffff",
                    color: "#3e2615",
                    minHeight: isMobileLandscape ? "32px" : "auto",
                  }}
                  onFocus={(e) => { e.target.style.borderColor = "#d4a05f"; }}
                  onBlur={(e) => { e.target.style.borderColor = "#c4a07a"; }}
                />
              </div>
            )}

            {message && (
              <div
                className={isMobileLandscape ? "text-[11px] p-2 rounded" : "text-sm p-3 rounded"}
                style={{
                  background: status === "success" ? "#dcfce7" : "#fee2e2",
                  color: status === "success" ? "#166534" : "#991b1b",
                }}
              >
                {message}
              </div>
            )}

            <button
              type="submit"
              disabled={status === "loading"}
              className={`${isMobileLandscape ? "px-3 py-1.5 text-xs" : "px-6 py-3 text-base"} rounded-xl font-semibold transition-all disabled:opacity-50`}
              style={{
                background: "#fffaeb",
                color: "#3e2615",
                boxShadow: "0 4px 0 #8b6542, 0 6px 12px rgba(0,0,0,0.3)",
                border: "2px solid #8b6542",
              }}
              onMouseEnter={(e) => { if (!e.currentTarget.disabled) e.currentTarget.style.transform = "translateY(-2px)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; }}
              onMouseDown={(e) => { if (!e.currentTarget.disabled) e.currentTarget.style.transform = "translateY(2px)"; }}
              onMouseUp={(e) => { if (!e.currentTarget.disabled) e.currentTarget.style.transform = "translateY(-2px)"; }}
            >
              {status === "loading" ? "Sending..." : "Submit deletion request"}
            </button>
          </form>
        </Card>

        {loginOpen && (
          <div className="fixed inset-0 z-[120] flex items-center justify-center px-4" style={{ background: "rgba(0,0,0,0.55)" }}>
            <div className="w-full max-w-md rounded-2xl border-4 shadow-2xl" style={{ background: "#fffaeb", borderColor: "#8b6542" }}>
              <div className="p-4 md:p-6 space-y-3">
                <div className="flex items-start justify-between">
                  <h3 className="text-xl font-bold" style={{ color: "#3e2615" }}>Log in to continue</h3>
                  <button
                    aria-label="Close login"
                    onClick={() => setLoginOpen(false)}
                    className="text-lg font-bold"
                    style={{ color: "#3e2615" }}
                  >
                    ×
                  </button>
                </div>
                <p className="text-sm" style={{ color: "#3e2615", opacity: 0.85 }}>
                  Sign in to the account you want to delete. We'll submit your deletion request right after you log in.
                </p>
                <form className="space-y-3" onSubmit={handleLogin}>
                  <div>
                    <label className="block text-sm font-medium mb-1" style={{ color: "#3e2615" }}>
                      Email or username
                    </label>
                    <input
                      type="text"
                      required
                      value={loginEmail}
                      onChange={(e) => setLoginEmail(e.target.value)}
                      className="w-full rounded-xl p-3 outline-none transition-all"
                      style={{
                        border: "2px solid #c4a07a",
                        background: "#ffffff",
                        color: "#3e2615",
                      }}
                      onFocus={(e) => { e.target.style.borderColor = "#d4a05f"; }}
                      onBlur={(e) => { e.target.style.borderColor = "#c4a07a"; }}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1" style={{ color: "#3e2615" }}>
                      Password
                    </label>
                    <input
                      type="password"
                      required
                      value={loginPassword}
                      onChange={(e) => setLoginPassword(e.target.value)}
                      className="w-full rounded-xl p-3 outline-none transition-all"
                      style={{
                        border: "2px solid #c4a07a",
                        background: "#ffffff",
                        color: "#3e2615",
                      }}
                      onFocus={(e) => { e.target.style.borderColor = "#d4a05f"; }}
                      onBlur={(e) => { e.target.style.borderColor = "#c4a07a"; }}
                    />
                  </div>
                  {loginError && (
                    <div className="text-sm p-2 rounded" style={{ background: "#fee2e2", color: "#991b1b" }}>
                      {loginError}
                    </div>
                  )}
                  <button
                    type="submit"
                    disabled={loginSubmitting}
                    className="w-full rounded-xl font-semibold py-3 transition-all disabled:opacity-60"
                    style={{
                      background: "#f4a261",
                      color: "#3e2615",
                      boxShadow: "0 3px 0 #8b6542, 0 6px 10px rgba(0,0,0,0.25)",
                      border: "2px solid #8b6542",
                    }}
                    onMouseEnter={(e) => { if (!e.currentTarget.disabled) e.currentTarget.style.transform = "translateY(-2px)"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; }}
                    onMouseDown={(e) => { if (!e.currentTarget.disabled) e.currentTarget.style.transform = "translateY(2px)"; }}
                    onMouseUp={(e) => { if (!e.currentTarget.disabled) e.currentTarget.style.transform = "translateY(-2px)"; }}
                  >
                    {loginSubmitting ? "Signing in..." : "Sign in and submit"}
                  </button>
                </form>
              </div>
            </div>
          </div>
        )}
          </div>
        </div>

        {isMobileLandscape && (
          <>
            {showTopIndicator && (
              <div className="pointer-events-none absolute left-1/2 -translate-x-1/2 flex justify-center" style={{ top: "-23px", zIndex: 45 }}>
                <svg width={28} height={18} viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ filter: "drop-shadow(0 2px 2px rgba(0,0,0,0.65))" }}>
                  <path d="M1 7L6 2L11 7" stroke="#ffd180" strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
            )}
            {showBottomIndicator && (
              <div className="pointer-events-none absolute left-1/2 -translate-x-1/2 flex justify-center" style={{ bottom: "-20px", zIndex: 45 }}>
                <svg width={28} height={18} viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ filter: "drop-shadow(0 2px 2px rgba(0,0,0,0.65))" }}>
                  <path d="M1 1L6 6L11 1" stroke="#ffd180" strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
            )}
          </>
        )}
      </div>
    </PageLayout>
  );
}
